import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useKeenSlider } from "keen-slider/react";
import { useMediaQuery } from "react-responsive";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  useDisclosure,
} from "@nextui-org/react";
import "keen-slider/keen-slider.min.css";
import data from "../../data/index.json";
import BlurImage from "../../components/BlurImage";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
};

const stagger = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true },
};

const cardAnim = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const tabs = [
  { id: "all", label: "All" },
  { id: "personal", label: "Personal" },
  { id: "corporate", label: "Corporate" },
];

function ProjectCard({ item, onClick, isLarge }) {
  return (
    <motion.div
      layout
      variants={cardAnim}
      onClick={() => onClick(item)}
      className={`bento-card group overflow-hidden cursor-pointer ${
        isLarge ? "md:col-span-2 md:row-span-2" : ""
      }`}
    >
      <div className="relative overflow-hidden rounded-xl mb-4 aspect-video">
        <BlurImage
          src={item.src}
          alt={item.title}
          className="w-full h-full transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 dark:group-hover:bg-white/10 transition-all duration-300" />
      </div>
      <div className="space-y-3">
        <h3 className="font-display text-lg font-bold text-black dark:text-white">
          {item.title}
        </h3>
        <p className="font-body text-sm text-black/50 dark:text-white/50 leading-relaxed line-clamp-3">
          {item.description}
        </p>
        {item.role && (
          <p className="font-body text-xs font-semibold text-black/60 dark:text-white/60 uppercase tracking-wider">
            Role: {item.role}
          </p>
        )}
        <div className="flex gap-2 pt-2">
          {item.links?.map((link, idx) => (
            <a
              key={idx}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-xs font-semibold px-4 py-2 rounded-lg
                         bg-black text-white dark:bg-white dark:text-black
                         hover:bg-black/80 dark:hover:bg-white/80 transition-all duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function MobileSlider({ projects, onClick }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, instanceRef] = useKeenSlider({
    slides: { perView: 1.1, spacing: 16 },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
  });

  return (
    <>
      <div ref={sliderRef} className="keen-slider">
        {projects.map((item, idx) => (
          <div key={idx} className="keen-slider__slide">
            <div
              className="bento-card cursor-pointer"
              onClick={() => onClick(item)}
            >
              <div className="rounded-xl overflow-hidden mb-4 aspect-video">
                <BlurImage
                  src={item.src}
                  alt={item.title}
                  className="w-full h-full"
                />
              </div>
              <div className="space-y-3">
                <h3 className="font-display text-lg font-bold text-black dark:text-white">
                  {item.title}
                </h3>
                <p className="font-body text-sm text-black/50 dark:text-white/50 line-clamp-3">
                  {item.description}
                </p>
                {item.role && (
                  <p className="font-body text-xs font-semibold text-black/60 dark:text-white/60 uppercase tracking-wider">
                    Role: {item.role}
                  </p>
                )}
                <div className="flex gap-2 pt-2">
                  {item.links?.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-xs font-semibold px-4 py-2 rounded-lg
                                 bg-black text-white dark:bg-white dark:text-black
                                 hover:bg-black/80 dark:hover:bg-white/80 transition-all"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="keen-slider-dots">
        {projects.map((_, idx) => (
          <button
            key={idx}
            onClick={() => instanceRef.current?.moveToIdx(idx)}
            className={currentSlide === idx ? "dot active" : "dot"}
          />
        ))}
      </div>
    </>
  );
}

export default function MyPortfolio() {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const openProject = (item) => {
    setSelectedProject(item);
    onOpen();
  };

  const allProjects = [
    ...data.personalPortfolio.map((p) => ({ ...p, category: "personal" })),
    ...data.corporatePortfolio.map((p) => ({ ...p, category: "corporate" })),
  ];

  const filtered =
    activeTab === "all"
      ? allProjects
      : allProjects.filter((p) => p.category === activeTab);

  return (
    <section id="MyPortfolio" className="section-padding bg-white dark:bg-black">
      <div className="section-container">
        <motion.div {...fadeUp} className="text-center mb-16">
          <h2 className="section-title">My Portfolio</h2>
          <p className="section-subtitle mx-auto">
            A collection of projects I've built and contributed to
          </p>
        </motion.div>

        <motion.div
          {...fadeUp}
          className="flex justify-center gap-2 mb-12"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`font-body text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-black text-white dark:bg-white dark:text-black"
                  : "bg-black/5 text-black/50 dark:bg-white/5 dark:text-white/50 hover:bg-black/10 dark:hover:bg-white/10"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          {isMobile ? (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <MobileSlider
                projects={filtered}
                onClick={openProject}
              />
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              variants={stagger}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filtered.map((item, idx) => (
                <ProjectCard
                  key={item.id}
                  item={item}
                  onClick={openProject}
                  isLarge={idx === 0}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="3xl"
        placement="center"
        classNames={{
          base: "bg-white/95 dark:bg-black/95 backdrop-blur-2xl border border-black/10 dark:border-white/10",
          closeButton: "hover:bg-black/10 dark:hover:bg-white/10",
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="font-display text-2xl font-bold text-black dark:text-white">
                {selectedProject?.title}
              </ModalHeader>
              <ModalBody className="pb-6 space-y-6">
                <div className="rounded-xl overflow-hidden border-2 border-black/10 dark:border-white/10">
                  <BlurImage
                    src={selectedProject?.src}
                    alt={selectedProject?.title}
                    className="w-full h-auto"
                  />
                </div>
                <p className="font-body text-base text-black/60 dark:text-white/60 leading-relaxed">
                  {selectedProject?.description}
                </p>
                {selectedProject?.role && (
                  <p className="font-body text-sm font-semibold text-black/60 dark:text-white/60 uppercase tracking-wider">
                    Role: {selectedProject.role}
                  </p>
                )}
                <div className="flex gap-3 pt-2">
                  {selectedProject?.links?.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-sm font-semibold px-6 py-3 rounded-xl
                                 bg-black text-white dark:bg-white dark:text-black
                                 hover:bg-black/80 dark:hover:bg-white/80 transition-all"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </section>
  );
}
